import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  Button,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
  Menu,
  Pagination
} from '@mui/material';
import { usageAPI, vendorAPI, categoryAPI, invoiceAPI } from '../api/api';
import PdfViewerWithDetails from '../components/PdfViewerWithDetails';
import EditApiUsageDialog from '../components/EditApiUsageDialog';
import { BarChart as BarChartIcon, Add as AddIcon, MoreVert as MoreVertIcon, Search as SearchIcon } from '@mui/icons-material';

const ApiUses = () => {
  const [apiUses, setApiUses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [invoices, setInvoices] = useState([]);
  const [currentUsage, setCurrentUsage] = useState({
    application_name: '',
    vendor_id: '',
    category_id: '',
    token_uses: '',
    cost: 0,
    usage_date: new Date().toISOString().split('T')[0],
    status: 'Active'
  });
  const [viewUsage, setViewUsage] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUsage, setSelectedUsage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVendor, setSelectedVendor] = useState(''); // Added vendor filter state

  // Sorting state
  const [sortBy, setSortBy] = useState('id'); // Default sort by ID
  const [sortOrder, setSortOrder] = useState('desc'); // Default sort order

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 50; // Fixed to 50 as requested

  const open = Boolean(anchorEl);

  // Function to fetch API usage data with pagination and filters
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchData = async (page = 1, vendorFilter = selectedVendor, searchFilter = searchTerm) => {
    try {
      // Calculate skip based on page
      const skip = (page - 1) * itemsPerPage;
      
      // Get paginated results with sorting and filters
      const paginatedResponse = await usageAPI.getApiUsages(
        skip, 
        itemsPerPage, 
        sortBy, 
        sortOrder,
        vendorFilter || null,
        searchFilter || null
      );
      
      setApiUses(paginatedResponse.data);
    } catch (err) {
      // Handle different error response formats
      let errorMessage = 'Failed to load API usage data';
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.response?.data && Array.isArray(err.response.data)) {
        errorMessage = err.response.data.map(error => error.msg).join(', ');
      } else if (typeof err.response?.data === 'object' && err.response.data.msg) {
        errorMessage = err.response.data.msg;
      }
      
      setError(errorMessage);
      console.error('Error fetching API usage data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch total count of API usage records with filters
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchTotalCount = async (vendorFilter = selectedVendor, searchFilter = searchTerm) => {
    try {
      // Get count of API usage records with filters applied
      const countResponse = await usageAPI.getApiUsagesCount(vendorFilter || null, searchFilter || null);
      setTotalItems(countResponse.data);
    } catch (err) {
      console.error('Error fetching total count:', err);
    }
  };

  // Fetch additional data that doesn't need pagination
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchOptions = async () => {
    try {
      const [vendorResponse, categoryResponse, invoiceResponse] = await Promise.all([
        vendorAPI.getVendors(),
        categoryAPI.getCategories(),
        invoiceAPI.getInvoices() // Fetch invoices to get invoice numbers
      ]);
      
      setVendors(vendorResponse.data);
      setCategories(categoryResponse.data);
      setInvoices(invoiceResponse.data); // Store invoices for lookup
    } catch (err) {
      console.error('Error fetching options:', err);
    }
  };

  useEffect(() => {
    // Initial fetch with pagination and filters
    fetchData(currentPage, selectedVendor, searchTerm);
    fetchOptions(); // Fetch options data that doesn't need pagination
    fetchTotalCount(selectedVendor, searchTerm); // Fetch total count with filters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, sortBy, sortOrder, selectedVendor, searchTerm]); // Re-fetch when currentPage, sort parameters, filters change

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedVendor, searchTerm]);

  // Refresh data when component comes into view (e.g., after navigating back from other pages)
  useEffect(() => {
    const handleFocus = () => {
      fetchData(currentPage);
      fetchOptions();
      fetchTotalCount();
    };

    // Add event listener for focus (when user returns to page)
    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, sortBy, sortOrder, fetchData, fetchOptions, fetchTotalCount]);

  const handleClick = (event, usage) => {
    setAnchorEl(event.currentTarget);
    setSelectedUsage(usage);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteClick = async (usage) => {
    if (!usage || !usage.id) return;
    
    try {
      await usageAPI.deleteUsage(usage.id);
      
      // Refresh the list with current page and filters
      fetchData(currentPage, selectedVendor, searchTerm);
      fetchOptions();
      fetchTotalCount(selectedVendor, searchTerm); // Refresh count as well
      handleClose();
    } catch (err) {
      let errorMessage = 'Failed to delete API usage';
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.response?.data && Array.isArray(err.response.data)) {
        errorMessage = err.response.data.map(error => error.msg).join(', ');
      } else if (typeof err.response?.data === 'object' && err.response.data.msg) {
        errorMessage = err.response.data.msg;
      }
      
      setError(errorMessage);
      console.error('Error deleting API usage:', err);
    }
  };

  const handleViewDetails = (usage) => {
    setSelectedUsage(usage);
    setShowPdfViewer(true);
    handleClose();
  };

  const handleOpenDialog = (usage = null) => {
    if (usage) {
      setCurrentUsage({
        id: usage.id,
        application_name: usage.application_name || '',
        vendor_id: usage.vendor_id || '',
        category_id: usage.category_id || '',
        token_uses: String(usage.token_uses || ''),
        usage_unit: usage.usage_unit || '',
        usage_description: usage.usage_description || '',
        invoice_number: usage.invoice_number || '',
        cost: usage.cost || 0,
        usage_date: usage.usage_date || new Date().toISOString().split('T')[0],
        status: usage.status || 'Active'
      });
    } else {
      setCurrentUsage({
        application_name: '',
        vendor_id: '',
        category_id: '',
        token_uses: '',
        invoice_number: '',
        cost: 0,
        usage_date: new Date().toISOString().split('T')[0],
        status: 'Active'
      });
    }
    setOpenDialog(true);
  };

  const handleOpenEditDialog = (usage) => {
    setCurrentUsage(usage);
    setOpenEditDialog(true);
    handleClose(); // Close the action menu
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentUsage({
      application_name: '',
      vendor_id: '',
      category_id: '',
      token_uses: '',
      usage_unit: '',
      usage_description: '',
      cost: 0,
      usage_date: new Date().toISOString().split('T')[0],
      status: 'Active'
    });
  };

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
    setViewUsage(null);
  };

  const handleSaveUsage = async () => {
    try {
      // Prepare the payload, ensuring proper data types
      let formattedDate = currentUsage.usage_date;
      
      // Convert date to proper ISO format if needed
      if (currentUsage.usage_date) {
        // Check if it's already in YYYY-MM-DD format
        if (!/^\d{4}-\d{2}-\d{2}$/.test(currentUsage.usage_date)) {
          // If not in YYYY-MM-DD format, parse it and convert
          const dateObj = new Date(currentUsage.usage_date);
          // Format to YYYY-MM-DD
          formattedDate = dateObj.toISOString().split('T')[0];
        }
      } else {
        // Use current date if no date provided
        formattedDate = new Date().toISOString().split('T')[0];
      }
      
      const payload = {
        application_name: currentUsage.application_name || '',
        vendor_id: parseInt(currentUsage.vendor_id) || null,
        category_id: parseInt(currentUsage.category_id) || null,
        token_uses: String(currentUsage.token_uses) || '0',
        usage_unit: currentUsage.usage_unit || null,
        usage_description: currentUsage.usage_description || null,
        cost: parseFloat(currentUsage.cost) || 0,
        usage_date: formattedDate,
        status: currentUsage.status || 'Active'
      };
      
      // Only include invoice_number if it's provided
      if (currentUsage.invoice_number) {
        payload.invoice_number = currentUsage.invoice_number;
      }
      
      if (currentUsage && currentUsage.id) {
        // Update existing usage
        await usageAPI.updateUsage(currentUsage.id, payload);
      } else {
        // Create new usage
        await usageAPI.createApiUsage(payload);
      }
      
      // Refresh the list with current page and filters
      fetchData(currentPage, selectedVendor, searchTerm);
      fetchOptions();
      fetchTotalCount(selectedVendor, searchTerm); // Refresh count as well
      handleCloseDialog();
    } catch (err) {
      let errorMessage = 'Failed to save API usage';
      if (err.response?.data?.detail) {
        if (Array.isArray(err.response.data.detail)) {
          // Handle validation errors array
          errorMessage = err.response.data.detail.map(error => error.msg || error.detail || JSON.stringify(error)).join(', ');
        } else {
          errorMessage = err.response.data.detail;
        }
      } else if (err.response?.data && Array.isArray(err.response.data)) {
        errorMessage = err.response.data.map(error => error.msg).join(', ');
      } else if (typeof err.response?.data === 'object' && err.response.data.msg) {
        errorMessage = err.response.data.msg;
      }
      
      setError(errorMessage);
      console.error('Error saving API usage:', err);
    }
  };

  // Function to handle sorting
  const handleSort = (field) => {
    // If clicking the same field as current sort, toggle the sort order
    if (sortBy === field) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } 
    // If clicking a different field, sort by that field in descending order by default
    else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Paper 
          elevation={0} 
          sx={{ 
            p: 4, 
            mt: 4,
            textAlign: 'center',
            backgroundColor: 'background.paper',
            borderRadius: 3
          }}
        >
          <Typography color="error" variant="h6">
            {typeof error === 'string' ? error : 'An error occurred while loading data'}
          </Typography>
        </Paper>
      </Container>
    );
  }

  // Function to get status color
  const getStatusColor = (status) => {
    switch(status) {
      case 'Active':
        return 'success';
      case 'Inactive':
        return 'error';
      case 'Pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Function to get vendor name by ID, showing standard name if available
  const getVendorName = (vendorId) => {
    const vendor = vendors.find(v => v.id === vendorId);
    if (vendor) {
      // If the vendor has a standard_name, return it; otherwise return the name
      return vendor.standard_name && vendor.standard_name.trim() !== '' ? vendor.standard_name : vendor.name;
    }
    return 'N/A';
  };

  // Function to get category name by ID, showing standard name if available
  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    if (category) {
      // If the category has a standard_name, return it; otherwise return the name
      return category.standard_name && category.standard_name.trim() !== '' ? category.standard_name : category.name;
    }
    return 'N/A';
  };

  // Calculate pagination data
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startItem = (currentPage - 1) * itemsPerPage + 1;

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };



  return (
    <Container maxWidth="xl" sx={{ mt: 2 }}>
      <Box mb={3}>
        <Box display="flex" alignItems="center" mb={2}>
          <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 56, height: 56 }}>
            <BarChartIcon />
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
              API Usage
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Track and monitor your API usage
            </Typography>
          </Box>
        </Box>
        
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" gap={2} alignItems="center">
            <TextField
              variant="outlined"
              placeholder="Search API usage..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: 2,
                  backgroundColor: 'background.paper'
                }
              }}
              sx={{
                width: 300,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.paper'
                }
              }}
            />
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Filter by Vendor</InputLabel>
              <Select
                value={selectedVendor}
                label="Filter by Vendor"
                onChange={(e) => setSelectedVendor(e.target.value)}
              >
                <MenuItem value="">
                  <em>All Vendors</em>
                </MenuItem>
                {vendors.map((vendor) => (
                  <MenuItem key={vendor.id} value={vendor.id}>
                    {vendor.standard_name && vendor.standard_name.trim() !== '' ? vendor.standard_name : vendor.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* Sort controls */}
            <Box display="flex" gap={1} alignItems="center">
              <Button
                variant={sortBy === 'date' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => handleSort('date')}
                sx={{ 
                  borderRadius: 2,
                  minWidth: 'auto',
                  px: 1.5,
                  py: 0.5,
                  '&.MuiButton-contained': {
                    backgroundColor: 'primary.main',
                    color: 'white'
                  }
                }}
              >
                Date {sortBy === 'date' && (sortOrder === 'asc' ? '↑' : '↓')}
              </Button>
              <Button
                variant={sortBy === 'cost' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => handleSort('cost')}
                sx={{ 
                  borderRadius: 2,
                  minWidth: 'auto',
                  px: 1.5,
                  py: 0.5,
                  '&.MuiButton-contained': {
                    backgroundColor: 'primary.main',
                    color: 'white'
                  }
                }}
              >
                Amount {sortBy === 'cost' && (sortOrder === 'asc' ? '↑' : '↓')}
              </Button>
            </Box>
            {/* Clear sort button */}
            {(sortBy !== 'id' || sortOrder !== 'desc') && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setSortBy('id');
                  setSortOrder('desc');
                }}
                sx={{ 
                  borderRadius: 2,
                  minWidth: 'auto',
                  px: 1.5,
                  py: 0.5,
                  ml: 1
                }}
              >
                Clear Sort
              </Button>
            )}
            {/* Clear filters button */}
            {(selectedVendor || searchTerm) && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setSelectedVendor('');
                  setSearchTerm('');
                }}
                sx={{ 
                  borderRadius: 2,
                  minWidth: 'auto',
                  px: 1.5,
                  py: 0.5,
                  ml: 1
                }}
              >
                Clear Filters
              </Button>
            )}
          </Box>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ 
              backgroundColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.dark'
              }
            }}
          >
            Add New Usage
          </Button>
        </Box>
      </Box>
      
      <Paper 
        elevation={0} 
        sx={{ 
          backgroundColor: 'background.paper',
          borderRadius: 3,
          overflow: 'hidden'
        }}
      >
        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="api usage table">
            <TableHead sx={{ backgroundColor: 'background.default' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, color: 'text.primary', width: '5%' }}>SN</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.primary', width: '15%' }}>Invoice Number</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.primary', width: '15%' }}>Vendor</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Application</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: 'text.primary' }}>Quantity</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: 'text.primary' }}>Unit</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: 'text.primary' }}>Category</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: 'text.primary' }}>Cost</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: 'text.primary' }}>Usage Date</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: 'text.primary' }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: 'text.primary' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {apiUses.map((usage, index) => (
                <TableRow
                  key={usage.id}
                  sx={{ 
                    '&:last-child td, &:last-child th': { border: 0 },
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                  onDoubleClick={() => {
                    setSelectedUsage(usage);
                    setShowPdfViewer(true);
                  }}
                >
                  <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {startItem + index}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {usage.invoice?.invoice_number || 'N/A'}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {getVendorName(usage.vendor_id)}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {usage.application_name || 'N/A'}
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary' }}>
                    {usage.token_uses || '0'}
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary' }}>
                    {usage.usage_unit || 'N/A'}
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary' }}>
                    {getCategoryName(usage.category_id)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    ${usage.cost ? parseFloat(usage.cost).toFixed(2) : '0.00'}
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary' }}>
                    {usage.usage_date ? new Date(usage.usage_date).toLocaleDateString() : 'N/A'}
                  </TableCell>
                  <TableCell align="right">
                    <Chip 
                      label={usage.status || 'Active'} 
                      color={getStatusColor(usage.status || 'Active')} 
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      onClick={(event) => handleClick(event, usage)}
                      size="small"
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        {apiUses.length === 0 && (
          <Box 
            sx={{ 
              p: 6, 
              textAlign: 'center',
              backgroundColor: 'background.default'
            }}
          >
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No API usage records found
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {searchTerm || selectedVendor ? 'No records match your current filters. Try different criteria.' : 'Your API usage records will appear here'}
            </Typography>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{ mt: 2 }}
            >
              Add Usage Record
            </Button>
          </Box>
        )}
      </Paper>

      {/* Pagination Controls */}
      {totalItems > itemsPerPage && (
        <Box display="flex" justifyContent="center" alignItems="center" mt={3} mb={3}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="large"
          />
        </Box>
      )}

      {/* Dialog for adding/editing API usage */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {currentUsage?.id ? 'Edit API Usage' : 'Add New API Usage'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              autoFocus
              margin="dense"
              label="Application Name"
              fullWidth
              variant="outlined"
              value={currentUsage.application_name}
              onChange={(e) => setCurrentUsage({...currentUsage, application_name: e.target.value})}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Vendor</InputLabel>
              <Select
                value={currentUsage.vendor_id}
                label="Vendor"
                onChange={(e) => setCurrentUsage({...currentUsage, vendor_id: e.target.value})}
              >
                {vendors.map((vendor) => (
                  <MenuItem key={vendor.id} value={vendor.id}>
                    {vendor.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={currentUsage.category_id}
                label="Category"
                onChange={(e) => setCurrentUsage({...currentUsage, category_id: e.target.value})}
              >
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              margin="dense"
              label="Quantity"
              type="text"
              fullWidth
              variant="outlined"
              value={currentUsage.token_uses}
              onChange={(e) => setCurrentUsage({...currentUsage, token_uses: e.target.value})}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Usage Unit"
              type="text"
              fullWidth
              variant="outlined"
              value={currentUsage.usage_unit || ''}
              onChange={(e) => setCurrentUsage({...currentUsage, usage_unit: e.target.value})}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Usage Description"
              type="text"
              fullWidth
              variant="outlined"
              value={currentUsage.usage_description || ''}
              onChange={(e) => setCurrentUsage({...currentUsage, usage_description: e.target.value})}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Invoice Number"
              type="text"
              fullWidth
              variant="outlined"
              value={currentUsage.invoice_number || ''}
              onChange={(e) => setCurrentUsage({...currentUsage, invoice_number: e.target.value})}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Cost"
              type="number"
              fullWidth
              variant="outlined"
              value={currentUsage.cost}
              onChange={(e) => setCurrentUsage({...currentUsage, cost: parseFloat(e.target.value) || 0})}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
            <TextField
              margin="dense"
              label="Usage Date"
              type="date"
              fullWidth
              variant="outlined"
              value={currentUsage.usage_date}
              onChange={(e) => setCurrentUsage({...currentUsage, usage_date: e.target.value})}
              sx={{ mb: 2 }}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={currentUsage.status}
                label="Status"
                onChange={(e) => setCurrentUsage({...currentUsage, status: e.target.value})}
              >
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveUsage} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
      
      {/* View Details Dialog */}
      <Dialog open={openViewDialog} onClose={handleCloseViewDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          API Usage Details
        </DialogTitle>
        <DialogContent>
          {viewUsage ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" sx={{ mb: 1 }}><strong>Application Name:</strong> {viewUsage.application_name || 'N/A'}</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}><strong>Vendor:</strong> {getVendorName(viewUsage.vendor_id)}</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}><strong>Category:</strong> {getCategoryName(viewUsage.category_id)}</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}><strong>Invoice Number:</strong> {viewUsage.invoice?.invoice_number || 'N/A'}</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}><strong>Quantity:</strong> {viewUsage.token_uses || 0}</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}><strong>Usage Unit:</strong> {viewUsage.usage_unit || 'N/A'}</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}><strong>Usage Description:</strong> {viewUsage.usage_description || 'N/A'}</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}><strong>Cost:</strong> ${viewUsage.cost ? parseFloat(viewUsage.cost).toFixed(2) : '0.00'}</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}><strong>Usage Date:</strong> {viewUsage.usage_date ? new Date(viewUsage.usage_date).toLocaleDateString() : 'N/A'}</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}><strong>Status:</strong> <Chip label={viewUsage.status || 'Active'} color={getStatusColor(viewUsage.status || 'Active')} size="small" sx={{ ml: 1 }} /></Typography>
            </Box>
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>Close</Button>
        </DialogActions>
      </Dialog>
      
      {/* PDF Viewer Dialog */}
      {selectedUsage && (
        <PdfViewerWithDetails
          open={showPdfViewer}
          onClose={() => setShowPdfViewer(false)}
          usageId={selectedUsage.id}
          type="api-usage"
        />
      )}

      {/* Edit API Usage Dialog with PDF viewer layout */}
      <EditApiUsageDialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        usage={currentUsage}
        onSave={async () => {
          // Refresh the list after saving with current filters
          fetchData(currentPage, selectedVendor, searchTerm);
          fetchOptions();
          fetchTotalCount(selectedVendor, searchTerm); // Refresh count as well
        }}
        onError={(errorMessage) => {
          setError(errorMessage);
        }}
      />

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 1,
            borderRadius: 2,
            overflow: 'visible',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            '& ul': { p: 0 }
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => handleViewDetails(selectedUsage)}>
          View Details
        </MenuItem>
        <MenuItem onClick={() => handleOpenEditDialog(selectedUsage)}>
          Edit
        </MenuItem>
        <MenuItem onClick={() => handleDeleteClick(selectedUsage)}>
          Delete
        </MenuItem>
      </Menu>
    </Container>
  );
};

export default ApiUses;